/**
 * Merchandise Processing Fee (MPF) exemptions that depend on ORIGIN rather
 * than on a claimed preference.
 *
 * Most of 19 CFR 24.23(c) is written for "originating goods" — the USMCA and
 * the Singapore/Chile/Australia/Bahrain/Korea/Peru/Panama/Colombia/Oman FTAs
 * all require the preference to qualify *and be claimed*, which is why the
 * tariff surfaces gate them on the FTA checkbox / SPI. Three carve-outs are
 * not written that way; they turn on what the merchandise *is a product of*,
 * so they must be applied on origin alone:
 *
 *   1. Products of Israel — (c)(5). "The ad valorem fee, surcharge, and
 *      specific fees provided for under paragraphs (b)(1) and (b)(2) of this
 *      section will not apply to products of Israel that are entered, or
 *      withdrawn from warehouse for consumption, on or after September 16,
 *      1998." CBP states the consequence directly: goods of Israel are exempt
 *      irrespective of whether the ILFTA is claimed and irrespective of their
 *      country of export.
 *
 *   2. Products of beneficiary countries under the Caribbean Basin Economic
 *      Recovery Act — General Note 7, HTSUS.
 *
 *   3. Products of least-developed beneficiary developing countries —
 *      General Note 4(b)(i), HTSUS.
 *
 * (2) and (3) matter in practice because neither can be driven off an SPI.
 * A CBERA line is only flagged 'E'/'E*' when the filer claims the duty
 * preference, and the GSP SPIs that carried the least-developed list ('A',
 * 'A*', 'A+') were removed from FTAMappings.json when GSP lapsed — so an
 * SPI-gated check would charge 0.3464% up to the per-entry cap on every line
 * from these origins.
 *
 * Call sites pass whatever origin they hold; see isOriginMpfExempt().
 */

export type MpfExemptOrigin = {
  /** HTSUS spelling, kept verbatim so the list can be diffed against the GN. */
  name: string;
  /** Primary code as the country tables in this app spell it. */
  iso2: string;
  /**
   * Extra ISO2 codes and name spellings that must resolve to this entry.
   * Origin reaches these helpers from CSV columns, hand-typed fields and
   * three different country tables, so the aliases are load-bearing.
   */
  aliases?: string[];
};

/**
 * General Note 7, HTSUS — CBERA beneficiary countries.
 *
 * Mirrors the `E`/`E*` country list in FTAMappings.json. Kept as its own
 * declaration because the FTAMappings entry describes *duty-preference*
 * eligibility for a claimed SPI, whereas this list is the statutory
 * beneficiary roster that (c) keys the fee waiver to — the two coincide today
 * but answer different questions.
 *
 * "Netherlands Antilles" is one entry, not two. The territory was dissolved in
 * 2010 (its successors Aruba and Curaçao are listed separately) but GN 7 still
 * carries it, and it must never be read as "Netherlands" — matching is exact,
 * never by prefix, so an NL origin does not fall in here.
 */
export const CBERA_BENEFICIARY_COUNTRIES: readonly MpfExemptOrigin[] = [
  { name: 'Antigua and Barbuda', iso2: 'AG' },
  { name: 'Aruba', iso2: 'AW' },
  { name: 'Bahamas', iso2: 'BS', aliases: ['The Bahamas', 'Bahamas, The'] },
  { name: 'Barbados', iso2: 'BB' },
  { name: 'Belize', iso2: 'BZ' },
  { name: 'Curacao', iso2: 'CW', aliases: ['Curaçao'] },
  { name: 'Dominica', iso2: 'DM' },
  { name: 'Grenada', iso2: 'GD' },
  { name: 'Guyana', iso2: 'GY' },
  { name: 'Haiti', iso2: 'HT' },
  { name: 'Jamaica', iso2: 'JM' },
  { name: 'Montserrat', iso2: 'MS' },
  { name: 'Netherlands Antilles', iso2: 'AN' },
  {
    name: 'Saint Kitts and Nevis',
    iso2: 'KN',
    aliases: ['St. Kitts and Nevis', 'St Kitts and Nevis'],
  },
  { name: 'Saint Lucia', iso2: 'LC', aliases: ['St. Lucia', 'St Lucia'] },
  {
    name: 'Saint Vincent and the Grenadines',
    iso2: 'VC',
    aliases: ['St. Vincent and the Grenadines', 'St Vincent and the Grenadines'],
  },
  { name: 'Trinidad and Tobago', iso2: 'TT' },
  {
    name: 'Virgin Islands, British',
    iso2: 'VG',
    aliases: ['British Virgin Islands'],
  },
];

/**
 * General Note 4(b)(i), HTSUS — least-developed beneficiary developing
 * countries.
 *
 * Several entries carry aliases because the GN spelling differs from the ISO
 * name our country tables use ("Congo (Kinshasa)" vs "Democratic Republic of
 * the Congo", "Gambia, The" vs "Gambia", "Republic of Yemen" vs "Yemen",
 * "The Solomon Islands" vs "Solomon Islands"). Burma carries both the CBP code
 * 'BU' used by our country tables and the ISO 3166 code 'MM'.
 */
export const LEAST_DEVELOPED_BENEFICIARY_COUNTRIES: readonly MpfExemptOrigin[] =
  [
    { name: 'Afghanistan', iso2: 'AF' },
    { name: 'Angola', iso2: 'AO' },
    { name: 'Benin', iso2: 'BJ' },
    { name: 'Bhutan', iso2: 'BT' },
    { name: 'Burkina Faso', iso2: 'BF' },
    { name: 'Burma', iso2: 'BU', aliases: ['MM', 'Myanmar'] },
    { name: 'Burundi', iso2: 'BI' },
    { name: 'Cambodia', iso2: 'KH' },
    { name: 'Central African Republic', iso2: 'CF' },
    { name: 'Chad', iso2: 'TD' },
    { name: 'Comoros', iso2: 'KM' },
    {
      name: 'Congo (Kinshasa)',
      iso2: 'CD',
      aliases: [
        'Democratic Republic of the Congo',
        'Congo, Democratic Republic of the',
        'Congo-Kinshasa',
        'DR Congo',
      ],
    },
    { name: 'Djibouti', iso2: 'DJ' },
    { name: 'Ethiopia', iso2: 'ET' },
    { name: 'Gambia, The', iso2: 'GM', aliases: ['Gambia', 'The Gambia'] },
    { name: 'Guinea', iso2: 'GN' },
    { name: 'Guinea-Bissau', iso2: 'GW' },
    { name: 'Haiti', iso2: 'HT' },
    { name: 'Kiribati', iso2: 'KI' },
    { name: 'Lesotho', iso2: 'LS' },
    { name: 'Liberia', iso2: 'LR' },
    { name: 'Madagascar', iso2: 'MG' },
    { name: 'Malawi', iso2: 'MW' },
    { name: 'Mali', iso2: 'ML' },
    { name: 'Mauritania', iso2: 'MR' },
    { name: 'Mozambique', iso2: 'MZ' },
    { name: 'Nepal', iso2: 'NP' },
    { name: 'Niger', iso2: 'NE' },
    { name: 'Republic of Yemen', iso2: 'YE', aliases: ['Yemen'] },
    { name: 'Rwanda', iso2: 'RW' },
    { name: 'Samoa', iso2: 'WS' },
    {
      name: 'Sao Tome and Principe',
      iso2: 'ST',
      aliases: ['São Tomé and Príncipe'],
    },
    { name: 'Senegal', iso2: 'SN' },
    { name: 'Sierra Leone', iso2: 'SL' },
    { name: 'Somalia', iso2: 'SO' },
    { name: 'South Sudan', iso2: 'SS' },
    { name: 'Tanzania', iso2: 'TZ' },
    {
      name: 'The Solomon Islands',
      iso2: 'SB',
      aliases: ['Solomon Islands'],
    },
    { name: 'Timor-Leste', iso2: 'TL', aliases: ['East Timor'] },
    { name: 'Togo', iso2: 'TG' },
    { name: 'Tuvalu', iso2: 'TV' },
    { name: 'Uganda', iso2: 'UG' },
    { name: 'Vanuatu', iso2: 'VU' },
    { name: 'Zambia', iso2: 'ZM' },
  ];

/**
 * Same NFD/strip pipeline countryCodes.ts uses, so "Curaçao", "CURACAO" and
 * "St. Lucia" all collapse onto one key. Letters only — punctuation and
 * spacing differences between a CSV, a dropdown and the HTSUS all disappear.
 */
const normalizeName = (s: string | null | undefined): string =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z]/g, '');

type OriginIndex = { iso2: Set<string>; names: Set<string> };

const buildIndex = (list: readonly MpfExemptOrigin[]): OriginIndex => {
  const iso2 = new Set<string>();
  const names = new Set<string>();
  for (const entry of list) {
    iso2.add(entry.iso2.toUpperCase());
    names.add(normalizeName(entry.name));
    for (const alias of entry.aliases || []) {
      // A two-letter alias is another country code ('MM' for Burma); anything
      // longer is an alternate spelling.
      if (alias.length === 2) {
        iso2.add(alias.toUpperCase());
      } else {
        names.add(normalizeName(alias));
      }
    }
  }
  return { iso2: iso2, names: names };
};

const CBERA_INDEX = buildIndex(CBERA_BENEFICIARY_COUNTRIES);
const LDBDC_INDEX = buildIndex(LEAST_DEVELOPED_BENEFICIARY_COUNTRIES);

/**
 * Exact membership test against a prebuilt index.
 *
 * Both the code and the name are checked because the tariff surfaces populate
 * `originIso2` from a local getIso2ByCountryName() that returns undefined when
 * the name didn't resolve — falling back to the name keeps a hand-typed
 * "Haiti" from being charged a fee it doesn't owe.
 *
 * Matching is exact on the normalized value, never a prefix or substring: that
 * is what stops "Netherlands" matching "Netherlands Antilles", "Nigeria"
 * matching "Niger", and "Dominican Republic" matching "Dominica".
 */
const matches = (
  index: OriginIndex,
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
): boolean => {
  const iso = (originIso2 || '').trim().toUpperCase();
  if (iso && index.iso2.has(iso)) return true;

  const rawName = (originCountry || '').trim();
  // Several call sites pass an ISO2 code through the `originCountry` slot when
  // that is all they hold, so a two-letter value is tried as a code first.
  if (rawName.length === 2 && index.iso2.has(rawName.toUpperCase())) return true;

  const name = normalizeName(rawName);
  return Boolean(name) && index.names.has(name);
};

/*
 * Effective dates of the three waivers themselves, 19 CFR 24.23(c). An entry
 * before its provision's date still owes the fee, whatever the origin.
 *
 *   (c)(1)(iii)  products of CBERA beneficiary countries    GN 7         1984-01-01
 *   (c)(1)(iv)   products of least-developed beneficiary
 *                developing countries                       GN 4(b)(i)   1997-07-01
 *   (c)(5)       products of Israel                                      1998-09-16
 */
const CBERA_MPF_EXEMPT_FROM = new Date('1984-01-01T00:00:00Z');
const LDBDC_MPF_EXEMPT_FROM = new Date('1997-07-01T00:00:00Z');
const ISRAEL_MPF_EXEMPT_FROM = new Date('1998-09-16T00:00:00Z');

/**
 * True when the entry is on or after a provision's effective date.
 *
 * An invalid or absent date must not silently re-introduce the fee — most
 * callers hold a live quote with no entry date at all — so only a date
 * genuinely before the effective date returns false.
 */
const onOrAfter = (
  entryDate: Date | null | undefined,
  from: Date,
): boolean => {
  if (entryDate && !Number.isNaN(entryDate.getTime())) {
    return entryDate >= from;
  }
  return true;
};

/**
 * True when the origin is a CBERA beneficiary country (GN 7, HTSUS) and the
 * entry is on or after 1984-01-01.
 */
export function isCberaBeneficiaryOrigin(
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
  entryDate?: Date | null,
): boolean {
  if (!matches(CBERA_INDEX, originIso2, originCountry)) return false;
  return onOrAfter(entryDate, CBERA_MPF_EXEMPT_FROM);
}

/**
 * True when the origin is a least-developed beneficiary developing country
 * (GN 4(b)(i), HTSUS) and the entry is on or after 1997-07-01.
 */
export function isLeastDevelopedBeneficiaryOrigin(
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
  entryDate?: Date | null,
): boolean {
  if (!matches(LDBDC_INDEX, originIso2, originCountry)) return false;
  return onOrAfter(entryDate, LDBDC_MPF_EXEMPT_FROM);
}

/**
 * True when a line is exempt from MPF because it is a product of Israel
 * (19 CFR 24.23(c)(5)) and the entry is on or after 1998-09-16.
 *
 * Accepts the ISO-2 code and/or the country name because the tariff surfaces
 * populate `originIso2` from `getIso2ByCountryName` and it can be blank when
 * the name didn't resolve — falling back to the name keeps a hand-typed
 * "Israel" from being charged a fee it doesn't owe.
 */
export function isIsraelOriginMpfExempt(
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
  entryDate?: Date | null,
): boolean {
  const iso = (originIso2 || '').trim().toUpperCase();
  const name = (originCountry || '').trim().toLowerCase();
  const isIsrael = iso === 'IL' || name === 'israel';
  if (!isIsrael) return false;
  return onOrAfter(entryDate, ISRAEL_MPF_EXEMPT_FROM);
}

/**
 * The single check every MPF calculation should run before it consults the
 * claimed-FTA list: true when the origin alone waives the fee.
 *
 * Deliberately independent of the SPI / FTA checkbox, and gated on each
 * provision's own effective date above.
 *
 * REMAINING LIMITATION. The date gate is per PROVISION, not per country. The
 * rosters are current membership, but countries are designated and withdrawn
 * by proclamation, so within a provision's window a historical entry can
 * still be scored wrong in both directions: a country designated after the
 * entry date is exempted too early, and one withdrawn since (Bangladesh, an
 * LDBDC until its GSP benefits were suspended in 2013) is charged a fee it
 * did not owe at the time. This is reachable — the ACE processor reads a
 * per-line `entry_date` from customer CSVs, and the engine around it already
 * date-filters tariff rules against that same date via
 * bd_effective_from/bd_effective_to. Closing it needs the per-country
 * designation history, which we do not hold; `entryDate` is already threaded
 * through to here, so supplying it is a data change, not a signature change.
 */
export function isOriginMpfExempt(
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
  entryDate?: Date | null,
): boolean {
  return (
    isIsraelOriginMpfExempt(originIso2, originCountry, entryDate) ||
    isCberaBeneficiaryOrigin(originIso2, originCountry, entryDate) ||
    isLeastDevelopedBeneficiaryOrigin(originIso2, originCountry, entryDate)
  );
}

/**
 * Human-readable reason an origin is MPF-exempt, or null when it isn't.
 * Useful for tooltips/explanations next to a $0.00 MPF line.
 */
export function getOriginMpfExemptionReason(
  originIso2: string | null | undefined,
  originCountry: string | null | undefined,
  entryDate?: Date | null,
): string | null {
  if (isIsraelOriginMpfExempt(originIso2, originCountry, entryDate)) {
    return 'Product of Israel — MPF waived on origin (19 CFR 24.23(c)(5)).';
  }
  if (isCberaBeneficiaryOrigin(originIso2, originCountry, entryDate)) {
    return 'Product of a CBERA beneficiary country — MPF waived on origin (General Note 7, HTSUS).';
  }
  if (isLeastDevelopedBeneficiaryOrigin(originIso2, originCountry, entryDate)) {
    return 'Product of a least-developed beneficiary developing country — MPF waived on origin (General Note 4(b)(i), HTSUS).';
  }
  return null;
}
